// Workaround for a Gravity Forms bug with invisible reCAPTCHA v2.
//
// GF's own gform.recaptcha.executeRecaptcha (js/gravityforms.js) calls
// grecaptcha.execute() and then returns a promise that only ever resolves —
// it polls .g-recaptcha-response forever, declares `reject` without using it,
// and has no timeout:
//
//     window.grecaptcha.execute( widgetId );
//     return new Promise(( resolve, reject ) => {
//         const intervalId = setInterval(() => { ...resolve when value... }, 100 );
//     });
//
// So if Google decides to show the image challenge and the visitor dismisses
// it (clicking outside the overlay closes it), the response field stays empty,
// the promise never settles, and the awaiting submission hangs forever. Worse,
// clicking submit again calls grecaptcha.execute() on a widget that was never
// reset, which the reCAPTCHA API silently ignores — so the challenge never
// comes back and the visitor can't submit at all.
//
// This replaces that method with one that additionally watches the challenge
// overlay: when it goes from visible to hidden with no token, the widget is
// reset (so the *next* submit can show the challenge again) and the promise is
// settled, letting the submission proceed to server-side validation, which
// fails with GF's normal "captcha" error the visitor can act on. A hard
// timeout guards against anything else leaving it pending.
const POLL_INTERVAL = 100;
const TIMEOUT = 120000;

// The challenge lives in an iframe Google appends to <body>; its closest
// fixed-position ancestor is the overlay whose visibility toggles.
function getChallengeOverlay(iframe) {
    let el = iframe.parentElement;

    while (el && el !== document.body) {
        if (window.getComputedStyle(el).position === 'fixed') {
            return el;
        }

        el = el.parentElement;
    }

    return null;
}

// Checks every challenge iframe, not just the first: each time Gravity Forms
// re-renders the form (which it does after a failed validation) reCAPTCHA
// appends another overlay and leaves the previous ones in the DOM. Looking at
// only the first match meant reading a stale, permanently-hidden overlay from
// the second dismissal onwards, so the dismissal went unnoticed.
function isChallengeVisible() {
    const iframes = document.querySelectorAll('iframe[src*="recaptcha/api2/bframe"]');

    for (let i = 0; i < iframes.length; i++) {
        const overlay = getChallengeOverlay(iframes[i]);

        if (!overlay) {
            continue;
        }

        const styles = window.getComputedStyle(overlay);

        if (styles.visibility !== 'hidden' && styles.opacity !== '0') {
            return true;
        }
    }

    return false;
}

function patchExecuteRecaptcha(gform) {
    gform.recaptcha.executeRecaptcha = function (widgetId, form) {
        const recaptcha = gform.utils.getNode('.ginput_recaptcha', form, true);

        // Same early bail as GF's original: a failed init should fall through
        // to server-side validation rather than block here.
        if (recaptcha && recaptcha.getAttribute('data-recaptcha-error') === '1') {
            return Promise.resolve();
        }

        // Reset before every execute, not just after a detected dismissal.
        // A widget left mid-execute silently ignores further execute() calls,
        // and that state can be reached in ways this code can't observe — a
        // dismissal it failed to spot, or a wrong answer the visitor then
        // closed. Resetting unconditionally makes each attempt independent of
        // whatever happened on the previous one, so retries never run out.
        try {
            window.grecaptcha.reset(widgetId);
        } catch (e) {
            // Nothing to reset yet on the first run; execute() below still works.
        }

        window.grecaptcha.execute(widgetId);

        return new Promise(function (resolve) {
            let challengeWasVisible = false;
            let elapsed = 0;

            const intervalId = setInterval(function () {
                const response = gform.utils.getNode('.g-recaptcha-response', form, true);

                if (response && response.value) {
                    clearInterval(intervalId);
                    resolve(response.value);
                    return;
                }

                elapsed += POLL_INTERVAL;

                if (isChallengeVisible()) {
                    challengeWasVisible = true;
                    return;
                }

                // Hidden again after having been shown, still no token: the
                // visitor dismissed it. Reset so the widget isn't stuck
                // mid-execute — without this, every later execute() is a no-op.
                if (challengeWasVisible || elapsed >= TIMEOUT) {
                    clearInterval(intervalId);

                    try {
                        window.grecaptcha.reset(widgetId);
                    } catch (e) {
                        // A reset failure shouldn't keep the promise pending.
                    }

                    resolve('');
                }
            }, POLL_INTERVAL);
        });
    };
}

export function initGravityFormsRecaptchaFix() {
    // GF defines gform.recaptcha in its own script, which may load after this
    // one; poll briefly rather than depend on enqueue order.
    let attempts = 0;

    const timerId = setInterval(function () {
        attempts++;

        if (window.gform && window.gform.recaptcha && window.gform.recaptcha.executeRecaptcha) {
            clearInterval(timerId);
            patchExecuteRecaptcha(window.gform);
            return;
        }

        if (attempts > 100) {
            clearInterval(timerId);
        }
    }, POLL_INTERVAL);
}
